import {
  Alert,
  Autocomplete,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import PageTitle from "../../widgets/PageTitle";
import SearchIcon from "@mui/icons-material/Search";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import { debounce } from "lodash";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import CustomCircularP from "../../widgets/CustomCircularP";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import DataTable from "../../widgets/DataTable";
import IconMenu from "../../widgets/IconMenu";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { GlassButton } from "../../themes/UiElements";
import { UserContext } from "../../context/UserProvider";

// ── new sub-component ──────────────────────────────────────────────────────────
const ActiveGroupBanner = ({ userData, loading, contactList, onGroupSet }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  // keep local selection in sync when dialog opens
  const handleOpen = () => {
    setSelected(userData?.contactGroup ?? "");
    setDialogOpen(true);
  };
  const handleClose = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/set-active-contact-group`,
        { groupName: selected },
        getHeader()
      );
      toast.success(response.data.message || "Active group updated!");
      onGroupSet(); // triggers reloadUser / refetch in parent
      handleClose();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  // Don't render anything while user data is still loading
  if (loading) return null;

  return (
    <>
      {userData?.contactGroup ? (
        /* ── has an active group ─────────────────────────────────── */
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "success.light",
            color: "success.contrastText",
          }}
        >
          <CheckCircleIcon fontSize="small" />
          <Typography variant="body2">
            Your current active contact group is&nbsp;
            <strong>{userData.contactGroup}</strong>.
          </Typography>
          <Button
            size="small"
            variant="text"
            sx={{ color: "inherit", textDecoration: "underline", p: 0, ml: 1 }}
            onClick={handleOpen}
          >
            Change
          </Button>
        </Stack>
      ) : (
        /* ── no active group ─────────────────────────────────────── */
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "warning.light",
            color: "warning.contrastText",
          }}
        >
          <GroupIcon fontSize="small" />
          <Typography variant="body2">
            You have not set the active contact group.&nbsp;
          </Typography>
          <Button
            size="small"
            variant="text"
            sx={{
              color: "inherit",
              fontWeight: 700,
              textDecoration: "underline",
              p: 0,
            }}
            onClick={handleOpen}
          >
            Set now
          </Button>
        </Stack>
      )}

      {/* ── picker dialog ───────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Select Active Contact Group</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {!contactList?.length ? (
            <Typography sx={{ p: 2 }} color="text.secondary">
              No groups available.
            </Typography>
          ) : (
            <List disablePadding>
              {contactList.map((group) => (
                <ListItem key={group} disablePadding>
                  <ListItemButton
                    selected={selected === group}
                    onClick={() => setSelected(group)}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "primary.light",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.main" },
                      },
                    }}
                  >
                    <ListItemText primary={group} />
                    {selected === group && (
                      <CheckCircleIcon fontSize="small" color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!selected || saving}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
// ──────────────────────────────────────────────────────────────────────────────

const ContactsList = () => {
  const { user: userData, loading, reloadUser } = useContext(UserContext);
  const [phoneQ, setPhoneQ] = useState("");
  const [groupName, setGroupName] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [deleting, setDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/user/get-contacts-group?searchphoneNumber=${phoneQ}&group=${groupName}`
  );
  const { data: contactList, isLoading: loadingList } =
    useApiEp(`/user/group-list`);

  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState("");

  const debouncedSearch = useCallback(
    debounce((value) => setPhoneQ(value), 500),
    []
  );

  const handleSearchChange = (value) => debouncedSearch(value);
  const handleMenuClose = () => setAnchorEl(null);

  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        navigate(`/home/configurations/contacts/${id}/contact-detail`);
        break;
      case "delete":
        setToDelete(id);
        setShowDelete(true);
        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container sx={{ mt: 3 }}>
      <CustomCircularP show={isLoading || deleting} />

      <TitleActionsContainer>
        <PageTitle>Contacts List</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" }, mt: { xs: 1, sm: 0 } }}
        >
          <Autocomplete
            size="small"
            loading={loadingList}
            sx={{ minWidth: 164 }}
            value={groupName}
            onChange={(_e, newV) => setGroupName(newV ?? "")}
            options={contactList || []}
            renderInput={(params) => (
              <TextField {...params} label="Group Name" />
            )}
          />
          <TextField
            sx={{ minWidth: 200 }}
            size="small"
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by Phone"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
          <GlassButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
          >
            Add
          </GlassButton>
          <IconMenu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            menuItems={[
              {
                label: "Create Contact Group",
                icon: <GroupAddIcon />,
                onClick: () => {
                  handleMenuClose();
                  navigate(`/home/add-contact`);
                },
              },
              {
                label: "Add Contact List to Group",
                icon: <PlaylistAddIcon />,
                onClick: () => {
                  handleMenuClose();
                  navigate(`/home/add-contact-list`);
                },
              },
            ]}
          />
        </Stack>
      </TitleActionsContainer>

      {/* ── active-group banner (new) ───────────────────────────────── */}
      <ActiveGroupBanner
        userData={userData}
        loading={loading}
        contactList={contactList}
        onGroupSet={reloadUser} // refreshes UserContext after save
      />

      <SimpleYesNoDialog
        show={showDelete}
        setShow={setShowDelete}
        title="Please confirm!"
        message="Are you sure you want to delete this item?"
        onYes={async () => {
          try {
            setDeleting(true);
            setShowDelete(false);
            const response = await axios.delete(
              `${API_ENDPOINT}/user/delete-contact/${toDelete}`,
              getHeader()
            );
            toast.success(response.data.message);
            refetch();
          } catch (error) {
            toastError(error);
          } finally {
            setDeleting(false);
          }
        }}
      />

      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },
          { id: "groupName", label: "Group" },
          { id: "phoneNumber", label: "Phone" },
          { id: "Name", label: "Name" },
          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              new Date(v).toLocaleString("en-IN", { dateStyle: "medium" }),
          },
        ]}
        actions={["view", "delete"]}
        rows={data?.data ?? []}
        page={page}
        limit={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
      />
    </Container>
  );
};

export default ContactsList;
